import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Enum, EnumListKey, useAllEnums } from '~/data/enums';
import { convertDateTypeDatePickerProps } from '~/utils/helper';

export type DimensionFilters = {
  date?: string;
  reportingType?: Enum;
  customFields?: Enum[];
};

type Props = {
  onApply: (filters: DimensionFilters) => void;
};

const initialFilters: DimensionFilters = {
  date: '',
  reportingType: undefined,
  customFields: [],
};

export const DimensionsForm = ({ onApply }: Props) => {
  const { t } = useTranslation();
  const { getEnums } = useAllEnums();
  const [open, setOpen] = useState(false);
  const customEntityEnums = getEnums(EnumListKey.CUSTOM_ENTITY_TIMESHEET);
  const { control, watch, handleSubmit, reset } = useForm<DimensionFilters>({
    defaultValues: initialFilters,
  });

  const formValues = watch();

  const groupedReportingTypes: (Enum & { group: string })[] = useMemo(() => {
    const workTypes = getEnums(EnumListKey.TIMESHEET_WORK_TYPE).map((item) => ({
      ...item,
      group: t('laWorkReportingTypes').toLocaleUpperCase(),
    }));
    const nonWorkTypes = getEnums(EnumListKey.TIMESHEET_NON_WORK_TYPE).map(
      (item) => ({
        ...item,
        group: t('laNonWorkReportingTypes').toLocaleUpperCase(),
      }),
    );
    const correctionTypes = getEnums(EnumListKey.TIMESHEET_CORRECTION_TYPE).map(
      (item) => ({
        ...item,
        group: t('laCorrectionReportingTypes').toLocaleUpperCase(),
      }),
    );
    return [...workTypes, ...nonWorkTypes, ...correctionTypes];
  }, [getEnums, t]);

  const customEntityOptions = useMemo(
    () =>
      customEntityEnums.map((item) => ({ ...item, group: 'Custom Fields' })),
    [customEntityEnums],
  );

  const handleOpen = () => {
    setOpen(true);
  };

  const handleCancel = () => {
    reset(initialFilters);
    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const onSubmit = (data: DimensionFilters) => {
    const filteredData = {
      ...(data.date && { date: data.date }),
      ...(data.reportingType && { reportingType: data.reportingType }),
      ...(data.customFields &&
        Object.keys(data.customFields).length > 0 && {
          customFields: data.customFields,
        }),
    };
    onApply(filteredData);
    handleClose();
  };

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleOpen}
        sx={{ textTransform: 'uppercase' }}
      >
        {t('setReportDimensions')}
      </Button>

      {open && (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
          <DialogTitle>{t('setReportDimensions')}</DialogTitle>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogContent
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <Controller
                name="date"
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <DatePicker
                    {...convertDateTypeDatePickerProps({
                      value: value ? new Date(value) : null,
                      onChange,
                    })}
                    format="YYYY-MM-DD"
                    label={t('laDate')}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                    data-testid="dateOfBirth"
                  />
                )}
              />

              <Controller
                name="reportingType"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={groupedReportingTypes}
                    groupBy={(option) =>
                      (option as Enum & { group: string }).group
                    }
                    getOptionLabel={(option) => option.name}
                    getOptionKey={(option) => option.code}
                    value={formValues.reportingType || null}
                    onChange={(_, value) => field.onChange(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('laReportingType')}
                        placeholder={t('laReportingType')}
                      />
                    )}
                    isOptionEqualToValue={(option, value) =>
                      option.code === value?.code
                    }
                  />
                )}
              />

              <Controller
                name="customFields"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    options={customEntityOptions}
                    getOptionLabel={(option) => option.name}
                    value={formValues.customFields || []}
                    onChange={(_, value) => field.onChange(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('laCustomFields')}
                        placeholder={t('laCustomFields')}
                      />
                    )}
                    isOptionEqualToValue={(option, value) =>
                      option.code === value?.code
                    }
                  />
                )}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCancel}>{t('laCancel')}</Button>
              <Button type="submit" variant="contained">
                {t('laApply')}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      )}
    </>
  );
};
